/**
 * PR-Time Cost Prediction — Terraform plan → cost delta demo page
 * ================================================================
 * Paste a `terraform show -json plan.tfplan` output → see the $ impact
 * before the PR merges. Designed as a live demo for prospects.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  GitBranch, TrendingUp, TrendingDown, Sparkles, Play, Upload, Copy,
  AlertTriangle, CheckCircle2, Info, Github, Mail, Loader2, FileCode2,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQSection from '../components/FAQSection';
import BillDetectiveCrossLink from '../components/BillDetectiveCrossLink';
import { faqsByPage } from '../data/faqs';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 10-line GitHub Actions snippet — the real, working integration path.
// Posted by the user's own CI using the built-in GITHUB_TOKEN, so we never
// need an installed GitHub App, never see their code, never hold any secret.
const GITHUB_ACTIONS_YAML = `# .github/workflows/cartie-pr-cost.yml
name: CARTIEAI · PR Cost
on: [pull_request]

jobs:
  cost:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: hashicorp/setup-terraform@v3
      - run: terraform init && terraform plan -out=plan.tfplan
      - run: terraform show -json plan.tfplan > plan.json
      - name: Get cost delta from CARTIEAI
        run: |
          curl -sS -X POST https://cartieai.com/api/pr-cost-predictor/analyze \\
            -H "Content-Type: application/json" --data @plan.json > result.json
      - uses: actions/github-script@v7
        with:
          script: |
            const { summary } = require('./result.json');
            github.rest.issues.createComment({
              ...context.repo,
              issue_number: context.issue.number,
              body: summary,
            });
`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : (n > 0 ? '+' : '');
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
};

const fmtMoneyPlain = (n) => {
  if (!n) return '$0';
  const abs = Math.abs(n);
  return `${n < 0 ? '-' : ''}$${abs.toFixed(2)}`;
};

const PRCostPredictor = () => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [planText, setPlanText] = useState('');

  // Waitlist modal state
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [wlEmail, setWlEmail] = useState('');
  const [wlName, setWlName] = useState('');
  const [wlCompany, setWlCompany] = useState('');

  const loadSample = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/pr-cost-predictor/sample`);
      setPlanText(JSON.stringify(data.plan, null, 2));
      setAnalysis(data.analysis);
      toast.success('Sample plan loaded', { description: 'A typical infra PR — ready for you to explore.' });
    } catch (e) { toast.error("We couldn't load the sample just now"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSample(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const runAnalysis = async () => {
    if (!planText.trim()) {
      toast.error('Paste a plan to see the cost', { description: 'Click "Load sample" if you\'d like to see a worked example first.' });
      return;
    }
    setLoading(true);
    try {
      const plan = JSON.parse(planText);
      const { data } = await axios.post(`${API_URL}/pr-cost-predictor/analyze`, plan);
      setAnalysis(data);
      toast.success('Analysis ready');
    } catch (e) {
      if (e instanceof SyntaxError) {
        toast.error("That doesn't look like valid JSON", { description: 'Run `terraform show -json plan.tfplan > plan.json` and paste the contents here.' });
      } else {
        toast.error("We hit a snag analysing that plan", { description: e?.response?.data?.detail || e?.message });
      }
    } finally { setLoading(false); }
  };

  const copyComment = () => {
    if (!analysis) return;
    const md = [
      '## 💰 PR-Time Cost Prediction',
      '',
      analysis.summary,
      '',
      '| Resource | Change | Δ / month |',
      '|---|---|---|',
      ...analysis.breakdown.slice(0, 10).map(b =>
        `| \`${b.address}\` | ${b.actions.join(', ')} | ${fmtMoney(b.delta_monthly_usd)} |`
      ),
      '',
      `*Before: ${fmtMoneyPlain(analysis.before_monthly_usd)}/mo · After: ${fmtMoneyPlain(analysis.after_monthly_usd)}/mo · Annual impact: ${fmtMoney(analysis.annualized_delta_usd)}*`,
      '',
      '_Analysed by [CARTIEAI](https://cartieai.com) · us-east-1 on-demand pricing_',
    ].join('\n');
    navigator.clipboard.writeText(md);
    toast.success('PR comment copied', { description: 'Paste it into any GitHub PR.' });
  };

  const copyYaml = () => {
    navigator.clipboard.writeText(GITHUB_ACTIONS_YAML);
    toast.success('Workflow copied', {
      description: 'Save it as .github/workflows/cartie-pr-cost.yml in your repo.',
    });
  };

  const submitWaitlist = async (e) => {
    e?.preventDefault?.();
    if (!wlEmail.trim()) {
      toast.error('A work email helps us reach you');
      return;
    }
    setWaitlistSubmitting(true);
    try {
      await axios.post(`${API_URL}/pr-cost-predictor/waitlist`, {
        email: wlEmail.trim(),
        name: wlName.trim() || undefined,
        company: wlCompany.trim() || undefined,
      });
      setWaitlistDone(true);
      toast.success("Welcome — you're a design partner", {
        description: "We'll reach out within 48 hours to learn how your team works.",
      });
    } catch (err) {
      toast.error("Couldn't save your spot just now", {
        description: err?.response?.data?.detail || err?.message || "Mind trying again in a moment?",
      });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const delta = analysis?.delta_monthly_usd || 0;
  const isIncrease = delta > 0;
  const isDecrease = delta < 0;

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 -mt-px relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-teal-500/10 blur-[120px]" />
      </div>
    <div className="p-6 sm:p-8 max-w-6xl mx-auto space-y-6 relative" data-testid="pr-cost-predictor-page">
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <GitBranch className="h-7 w-7 text-emerald-400" />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">PR-Time <span className="text-emerald-400">Cost Prediction</span></h1>
          <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200">
            <Sparkles className="h-3 w-3 mr-1" />New
          </Badge>
        </div>
        <p className="text-slate-300 max-w-3xl">A free, public tool from CARTIEAI. See the <strong className="text-white">exact monthly $ impact</strong> of your next Terraform PR before it merges. Paste the output of <code className="text-xs bg-slate-800 text-emerald-300 px-1 py-0.5 rounded">terraform show -json plan.tfplan</code> below — no account, no API keys, nothing to install. To make every PR comment automatic, use the 10-line GitHub Actions snippet further down the page.</p>
      </div>

      <BillDetectiveCrossLink
        testid="prcp-bd-crosslink"
        message="This predicts cost BEFORE you merge. To see who actually shipped which cost AFTER merge →"
      />

      {/* DELTA HERO */}
      {analysis && (
        <Card
          className={`border-2 ${isIncrease ? 'border-rose-500/60 bg-gradient-to-br from-rose-500/10 to-transparent' : isDecrease ? 'border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 to-transparent' : 'border-border'}`}
          data-testid="delta-hero"
        >
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <p className="text-sm text-emerald-300 uppercase tracking-wider mb-1 font-bold">Monthly cost delta</p>
                <div className="flex items-center gap-3">
                  {isIncrease && <TrendingUp className="h-10 w-10 text-rose-400" />}
                  {isDecrease && <TrendingDown className="h-10 w-10 text-emerald-400" />}
                  {!isIncrease && !isDecrease && <CheckCircle2 className="h-10 w-10 text-slate-400" />}
                  <h2 className={`text-5xl sm:text-6xl font-bold font-mono ${isIncrease ? 'text-rose-400' : isDecrease ? 'text-emerald-400' : 'text-white'}`} data-testid="delta-amount">
                    {fmtMoney(delta)}
                  </h2>
                </div>
                <p className="text-sm text-slate-100 mt-3">{analysis.summary}</p>
                <p className="text-xs text-slate-400 mt-1">Annualised: <span className="font-mono text-white">{fmtMoney(analysis.annualized_delta_usd)}</span> · {analysis.resources_analyzed} resources analysed · {analysis.resources_priced} priced</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button onClick={copyComment} variant="outline" data-testid="copy-pr-comment" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500 bg-transparent"><Copy className="h-4 w-4 mr-2" />Copy PR comment</Button>
                <Button
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500 bg-transparent"
                  onClick={copyYaml}
                  data-testid="copy-yaml-hero"
                >
                  <FileCode2 className="h-4 w-4 mr-2" />Copy GitHub Actions YAML
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="p-4 rounded-md bg-slate-900 border border-emerald-500/20">
                <p className="text-xs text-emerald-300 uppercase tracking-wider font-bold">Before</p>
                <p className="text-2xl font-mono font-bold text-white tabular-nums">{fmtMoneyPlain(analysis.before_monthly_usd)}<span className="text-sm text-slate-400 font-normal"> /mo</span></p>
              </div>
              <div className="p-4 rounded-md bg-slate-900 border border-emerald-500/20">
                <p className="text-xs text-emerald-300 uppercase tracking-wider font-bold">After</p>
                <p className="text-2xl font-mono font-bold text-white tabular-nums">{fmtMoneyPlain(analysis.after_monthly_usd)}<span className="text-sm text-slate-400 font-normal"> /mo</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* LEFT — INPUT */}
        <Card data-testid="plan-input-card" className="bg-slate-900 border-emerald-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-white"><Upload className="h-5 w-5 text-emerald-400" />Terraform plan JSON</CardTitle>
            <CardDescription className="text-slate-400">Paste the output of <code className="text-xs bg-slate-800 text-emerald-300 px-1 rounded">terraform show -json plan.tfplan</code></CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              value={planText}
              onChange={(e) => setPlanText(e.target.value)}
              placeholder='{ "resource_changes": [...] }'
              className="w-full h-72 p-3 rounded-md bg-slate-950 border border-emerald-500/20 text-xs font-mono text-emerald-300 focus:border-emerald-500 focus:outline-none"
              data-testid="plan-textarea"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadSample} disabled={loading} data-testid="load-sample-btn" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500 bg-transparent">
                <Sparkles className="h-4 w-4 mr-2" />Load sample
              </Button>
              <Button onClick={runAnalysis} disabled={loading} className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" data-testid="analyze-btn">
                <Play className="h-4 w-4 mr-2 fill-current" />{loading ? 'Analysing…' : 'Analyse'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* RIGHT — BREAKDOWN */}
        <Card data-testid="breakdown-card" className="bg-slate-900 border-emerald-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-white"><Info className="h-5 w-5 text-emerald-400" />Resource-by-resource</CardTitle>
            <CardDescription className="text-slate-400">Biggest impact first</CardDescription>
          </CardHeader>
          <CardContent>
            {!analysis ? (
              <Skeleton className="h-64" />
            ) : analysis.breakdown.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">No resource changes detected in this plan.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {analysis.breakdown.map((b, i) => {
                  const d = b.delta_monthly_usd;
                  return (
                    <div key={i} className={`p-3 rounded-md border ${d > 0 ? 'border-rose-500/30 bg-rose-500/10' : d < 0 ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-emerald-500/20 bg-slate-800/40'}`} data-testid={`breakdown-row-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <code className="text-xs font-semibold text-white">{b.address}</code>
                            {b.actions.map(a => (
                              <Badge key={a} variant="outline" className={a === 'create' ? 'text-emerald-300 border-emerald-500/40' : a === 'delete' ? 'text-rose-300 border-rose-500/40' : 'text-amber-300 border-amber-500/40'}>
                                {a}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-slate-400">{b.note}</p>
                        </div>
                        <div className={`shrink-0 text-right font-mono font-bold ${d > 0 ? 'text-rose-400' : d < 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                          {fmtMoney(d)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {analysis?.unpriced_types?.length > 0 && (
              <div className="mt-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
                  <span><strong>{analysis.unpriced_types.length} resource type(s) we don't price yet:</strong> {analysis.unpriced_types.join(', ')}. We still surface them in the breakdown so reviewers can see the full picture — we'd rather show $0 than guess. Coverage grows every time a new customer plan teaches us a fresh pattern.</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* GITHUB ACTIONS — real, copy-paste integration that works today */}
      <Card id="github-actions" className="bg-slate-900 border-emerald-500/30" data-testid="github-actions-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-white"><FileCode2 className="h-5 w-5 text-emerald-400" />Add it to your GitHub Actions in 60 seconds</CardTitle>
              <CardDescription className="mt-1 text-slate-300">No app to install, no secrets to share. Your own CI calls our public API and posts the comment using the <code className="text-xs bg-slate-800 text-emerald-300 px-1 rounded">GITHUB_TOKEN</code> GitHub already gives every workflow.</CardDescription>
            </div>
            <Button onClick={copyYaml} className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" data-testid="copy-yaml-btn">
              <Copy className="h-4 w-4 mr-2" />Copy workflow
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <pre className="bg-slate-950 border border-emerald-500/20 rounded-md p-4 text-[12px] font-mono text-emerald-300 overflow-x-auto" data-testid="github-actions-yaml">
{GITHUB_ACTIONS_YAML}
          </pre>
          <div className="grid sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rounded-md bg-slate-800/50 border border-emerald-500/20">
              <p className="font-semibold mb-1 flex items-center gap-1.5 text-white"><span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-[11px] font-bold">1</span>Save the file</p>
              <p className="text-slate-400">Drop it at <code className="text-[11px]">.github/workflows/cartie-pr-cost.yml</code> in your repo.</p>
            </div>
            <div className="p-3 rounded-md bg-slate-800/50 border border-emerald-500/20">
              <p className="font-semibold mb-1 flex items-center gap-1.5 text-white"><span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-[11px] font-bold">2</span>Open a PR</p>
              <p className="text-slate-400">Your CI runs <code className="text-[11px]">terraform plan</code>, sends the JSON to our public API, gets a friendly summary.</p>
            </div>
            <div className="p-3 rounded-md bg-slate-800/50 border border-emerald-500/20">
              <p className="font-semibold mb-1 flex items-center gap-1.5 text-white"><span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-[11px] font-bold">3</span>Get a comment</p>
              <p className="text-slate-400">Posted by <code className="text-[11px]">github-actions[bot]</code> — under your control, in your repo, with your token.</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 italic flex items-start gap-2 pt-2 border-t border-emerald-500/20">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-400" />
            <span>Why we love this approach: you keep total control. We never touch your code, your tokens, or your GitHub org. No third-party app to security-review. If you ever want to stop, delete the file — that's it.</span>
          </p>
        </CardContent>
      </Card>

      {/* DESIGN PARTNER — honest, optional invite for hands-on help */}
      <Card id="design-partner" className="bg-slate-900 border-emerald-500/30" data-testid="design-partner-card">
        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold flex items-center gap-2 text-white"><Sparkles className="h-4 w-4 text-emerald-400" />Want us to prioritise your team's resource types?</p>
            <p className="text-sm text-slate-300 mt-1 max-w-2xl">
              We're co-building this with our first 10 design-partner teams. If you tell us what your Terraform PRs actually look like, we'll add coverage for the resources you use and ship a tailored summary format. No fee, no commitment — just a real conversation.
            </p>
          </div>
          <Button
            onClick={() => { setWaitlistDone(false); setWaitlistOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
            data-testid="open-design-partner-btn"
          >
            <Mail className="h-4 w-4 mr-2" />Become a design partner
          </Button>
        </CardContent>
      </Card>

      {/* DESIGN-PARTNER MODAL */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent data-testid="waitlist-dialog">
          {waitlistDone ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="h-5 w-5" />Welcome — you're in.
                </DialogTitle>
                <DialogDescription>
                  Thank you for your interest. Our founder will email <strong>{wlEmail}</strong> within 48 hours to learn how your team works and which resource types matter most. The free preview on this page will keep working in the meantime — no strings attached.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setWaitlistOpen(false)} data-testid="waitlist-close-btn">
                  Sounds good
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submitWaitlist}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-500" />Become a design partner
                </DialogTitle>
                <DialogDescription>
                  We're co-building PR-Time Cost Prediction with our first 10 teams. Tell us a little about your work and we'll prioritise the resource types you use, polish the summary format you'd want, and stay in touch as the tool grows. No fee, no commitment — just an honest conversation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Work email *</label>
                  <Input
                    type="email"
                    required
                    value={wlEmail}
                    onChange={(e) => setWlEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    autoFocus
                    data-testid="waitlist-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Your name</label>
                    <Input
                      type="text"
                      value={wlName}
                      onChange={(e) => setWlName(e.target.value)}
                      placeholder="Jane Doe"
                      data-testid="waitlist-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Company</label>
                    <Input
                      type="text"
                      value={wlCompany}
                      onChange={(e) => setWlCompany(e.target.value)}
                      placeholder="Acme Corp"
                      data-testid="waitlist-company-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setWaitlistOpen(false)}
                  data-testid="waitlist-cancel-btn"
                >
                  Maybe later
                </Button>
                <Button
                  type="submit"
                  disabled={waitlistSubmitting}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white"
                  data-testid="waitlist-submit-btn"
                >
                  {waitlistSubmitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving your spot…</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" />Save my spot</>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <section className="px-4 pb-12 max-w-4xl mx-auto">
        <FAQSection
          title="PR Cost Predictor FAQ"
          items={faqsByPage.pr_cost_predictor}
          testIdPrefix="pr-cost-faq"
          variant="card"
        />
      </section>
    </div>
    </div>
  );
};

export default PRCostPredictor;
